import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useLanguages } from '../hooks/useLanguages';
import { useCatalog } from '../hooks/useCatalog';
import { useAppConfig } from '../hooks/useAppConfig';
import { fetchTextWithEnglishFallback } from '../services/dataApi';
import StreakXpBar from '../components/StreakXpBar';
import { LearningProgressService } from '../services/LearningProgressService';
import { AchievementsService } from '../services/AchievementsService';
import { SrsService } from '../services/SrsService';
import { PlacementTestService } from '../services/PlacementTestService';

// Per-step Tailwind classes — indexed by step position (0-based).
// Content (label, desc, icon, code) comes from app_config.json instead.
const STEP_STYLES = [
  { badgeBg: 'bg-sky-600',     textColor: 'text-sky-700',     bgColor: 'bg-sky-50',     borderColor: 'border-sky-200',     btnClass: 'bg-sky-600 hover:bg-sky-700'        },
  { badgeBg: 'bg-indigo-600',  textColor: 'text-indigo-700',  bgColor: 'bg-indigo-50',  borderColor: 'border-indigo-200',  btnClass: 'bg-indigo-600 hover:bg-indigo-700'  },
  { badgeBg: 'bg-violet-600',  textColor: 'text-violet-700',  bgColor: 'bg-violet-50',  borderColor: 'border-violet-200',  btnClass: 'bg-violet-600 hover:bg-violet-700'  },
  { badgeBg: 'bg-emerald-600', textColor: 'text-emerald-700', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-200', btnClass: 'bg-emerald-600 hover:bg-emerald-700' },
  { badgeBg: 'bg-rose-600',    textColor: 'text-rose-700',    bgColor: 'bg-rose-50',    borderColor: 'border-rose-200',    btnClass: 'bg-rose-600 hover:bg-rose-700'      },
  { badgeBg: 'bg-cyan-600',    textColor: 'text-cyan-700',    bgColor: 'bg-cyan-50',    borderColor: 'border-cyan-200',    btnClass: 'bg-cyan-600 hover:bg-cyan-700'      },
  { badgeBg: 'bg-orange-500',  textColor: 'text-orange-700',  bgColor: 'bg-orange-50',  borderColor: 'border-orange-200',  btnClass: 'bg-orange-500 hover:bg-orange-600'  },
  { badgeBg: 'bg-teal-600',    textColor: 'text-teal-700',    bgColor: 'bg-teal-50',    borderColor: 'border-teal-200',    btnClass: 'bg-teal-600 hover:bg-teal-700'      },
  { badgeBg: 'bg-fuchsia-600', textColor: 'text-fuchsia-700', bgColor: 'bg-fuchsia-50', borderColor: 'border-fuchsia-200', btnClass: 'bg-fuchsia-600 hover:bg-fuchsia-700' },
  { badgeBg: 'bg-amber-500',   textColor: 'text-amber-700',   bgColor: 'bg-amber-50',   borderColor: 'border-amber-200',   btnClass: 'bg-amber-500 hover:bg-amber-600'    },
  { badgeBg: 'bg-lime-600',    textColor: 'text-lime-700',    bgColor: 'bg-lime-50',    borderColor: 'border-lime-200',    btnClass: 'bg-lime-600 hover:bg-lime-700'      },
  { badgeBg: 'bg-pink-600',    textColor: 'text-pink-700',    bgColor: 'bg-pink-50',    borderColor: 'border-pink-200',    btnClass: 'bg-pink-600 hover:bg-pink-700'      },
];

function parseIntro(text) {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  const title = lines[0] || '';
  const rule = lines.length >= 3 ? lines[lines.length - 1] : '';
  const tipLines = lines.slice(1, -2);
  const tips = [];
  for (let i = 0; i < tipLines.length; i += 2) {
    if (tipLines[i]) tips.push({ heading: tipLines[i], body: tipLines[i + 1] || '' });
  }
  return { title, tips, rule };
}

function useIntro(langCode) {
  const [intro, setIntro] = useState(null);
  useEffect(() => {
    if (!langCode) return;
    setIntro(null);
    fetchTextWithEnglishFallback(
      (locale) => `language-introductions/${locale}/intro.txt`,
      langCode,
    )
      .then((text) => setIntro(parseIntro(text)))
      .catch(() => setIntro(null));
  }, [langCode]);
  return intro;
}

const TIP_ICONS = ['💡', '🔄', '🌍', '📖', '👂', '🗣️', '📅'];
const STARTED_ONLY_STEPS = new Set([
  'foundations',
  'basics',
  'alphabet',
  'grammar',
  'practice',
]);

export default function Home() {
  const navigate = useNavigate();
  const { languagePrefs, isAuthenticated } = useAuth();
  const { languages } = useLanguages();
  const { catalog } = useCatalog();
  const config = useAppConfig(languagePrefs?.nativeLanguage);

  const { t } = useTranslation();

  const langMap = Object.fromEntries(languages.map((l) => [l.code, l]));
  const targetLang = languagePrefs?.targetLanguage ? langMap[languagePrefs.targetLanguage] : null;
  const targetLanguageLabel = targetLang?.label
    ?? languagePrefs?.targetLanguage?.toUpperCase()
    ?? '';
  const learningPathSubtitle = config.learningPathSubtitle
    .split('{{targetlanguage}}')
    .join(targetLanguageLabel);

  const intro = useIntro(languagePrefs?.nativeLanguage);

  const targetFeatures = catalog?.[languagePrefs?.targetLanguage]?.features ?? null;
  const langPair = languagePrefs
    ? `${languagePrefs.nativeLanguage}_${languagePrefs.targetLanguage}`
    : 'guest';
  const [pathProgress, setPathProgress] = useState(() => LearningProgressService.snapshot(langPair));

  useEffect(() => {
    const refresh = () => setPathProgress(LearningProgressService.snapshot(langPair));
    refresh();
    window.addEventListener('learning-progress-changed', refresh);
    return () => window.removeEventListener('learning-progress-changed', refresh);
  }, [langPair]);

  const [dueCount, setDueCount] = useState(0);
  useEffect(() => {
    setDueCount(languagePrefs ? SrsService.getDueCount(langPair) : 0);
  }, [langPair, languagePrefs]);

  const showPlacementBanner = languagePrefs
    && !PlacementTestService.isCompleted(langPair)
    && pathProgress.overall < 20;

  const openStep = (item) => {
    if (item.step >= 6 && !isAuthenticated) {
      navigate('/login');
      return;
    }
    if (STARTED_ONLY_STEPS.has(item.code)) {
      LearningProgressService.record(langPair, item.code, 1, {
        activityId: `started:${item.code}`,
      });
    }
    if (item.code === 'conversations') {
      AchievementsService.tryUnlock('first_conversation').then(AchievementsService.showBadgeToast);
    }
    if (item.code === 'foundations') {
      navigate('/learn/common-foundations');
    } else if (item.code === 'tests') {
      navigate('/exercises');
    } else if (item.code === 'alphabet') {
      navigate(targetLang ? `/learn/${targetLang.code}?tab=alphabet` : '/');
    } else if (item.code === 'basics') {
      navigate(targetLang ? `/learn/${targetLang.code}` : '/');
    } else if (item.code === 'listen') {
      navigate(targetLang ? `/listen/${targetLang.code}` : '/');
    } else if (item.code === 'speak') {
      navigate('/speak');
    } else if (item.code === 'practice') {
      navigate('/practice');
    } else {
      navigate(`/learn/${item.code}`);
    }
  };

  // Journey content is selected by native language in useAppConfig.
  const allSteps = config.learningSteps.map((s, i) => ({
    ...s,
    ...(STEP_STYLES[i] ?? STEP_STYLES[STEP_STYLES.length - 1]),
  }));

  const visibleSteps = allSteps.filter((s) => {
    if (!s.catalogFeature) return true;                    // no catalog requirement — always show
    if (!targetFeatures) return false;                     // lang has no catalog entry
    return targetFeatures.includes(s.catalogFeature);
  });

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(ellipse at 12% 12%, #dbeafe 0%, #f8fafc 42%, #ede9fe 100%)' }}>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden border-b border-slate-200/60">
        <div className="pointer-events-none absolute -left-40 -top-20 h-[28rem] w-[28rem] rounded-full bg-indigo-400/20 blur-3xl" />
        <div className="pointer-events-none absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-violet-400/20 blur-3xl" />
        <div className="pointer-events-none absolute left-1/2 top-1/3 h-72 w-72 -translate-x-1/2 rounded-full bg-cyan-300/15 blur-3xl" />

        <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-28">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50/80 px-4 py-1.5 backdrop-blur-sm">
            <span className="h-2 w-2 animate-pulse rounded-full bg-indigo-500" />
            <span className="text-xs font-black uppercase tracking-widest text-indigo-700">{t('home.badge')}</span>
          </div>

          <h1 className="max-w-4xl text-5xl font-black leading-[1.1] text-slate-900 md:text-6xl lg:text-7xl">
            {t('home.headline1')}{' '}
            <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 bg-clip-text text-transparent">
              {t('home.headline2')}
            </span>
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg lg:text-xl">
            {t('home.subtitle')}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <button
              onClick={() => document.getElementById('learn-section').scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-7 py-3.5 font-bold text-white shadow-lg shadow-indigo-600/30 transition hover:-translate-y-0.5 hover:shadow-indigo-600/50 hover:from-indigo-700 hover:to-violet-700"
            >
              {t('home.ctaLearn')}
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>

        </div>
      </section>

      <main className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">

        {/* ── Native language intro ── */}
        {intro && (
          <section className="mb-14 rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-slate-100 bg-gradient-to-r from-indigo-50 to-violet-50 px-6 py-5 md:px-10">
              <div className="flex items-center gap-3">
                <div>
                  <p className="text-xs font-black uppercase tracking-widest text-indigo-600">{t('home.tipsLabel')}</p>
                  <h2 className="text-xl font-black text-slate-900">{intro.title}</h2>
                </div>
              </div>
            </div>

            <div className="p-6 md:p-10">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {intro.tips.map((tip, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="mt-0.5 text-xl leading-none shrink-0">{TIP_ICONS[i % TIP_ICONS.length]}</span>
                    <div>
                      <p className="text-sm font-bold text-slate-800">{tip.heading}</p>
                      <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{tip.body}</p>
                    </div>
                  </div>
                ))}
              </div>

              {intro.rule && (
                <div className="mt-6 rounded-2xl border border-indigo-100 bg-indigo-50 px-5 py-4">
                  <p className="text-xs font-black uppercase tracking-widest text-indigo-500 mb-1">{t('home.simpleRule')}</p>
                  <p className="text-sm font-semibold text-indigo-900">{intro.rule}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ── Streak / XP bar (authenticated users only) ── */}
        {isAuthenticated && <StreakXpBar />}

        {/* ── Today's lesson: surfaces SRS due-cards ahead of generic browsing ── */}
        {dueCount > 0 && (
          <section className="mb-8 flex flex-col items-start gap-3 rounded-2xl border border-indigo-200 bg-gradient-to-r from-indigo-600 to-violet-600 p-5 text-white shadow-lg sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-widest text-white/70">{t('home.todaysLessonLabel')}</p>
              <h2 className="text-lg font-black">{t('home.dueWordsTitle', { count: dueCount })}</h2>
            </div>
            <button
              onClick={() => navigate('/practice?review=1')}
              className="shrink-0 rounded-xl bg-white px-5 py-2.5 text-sm font-bold text-indigo-700 shadow-sm transition hover:-translate-y-0.5"
            >
              {t('home.reviewNow')}
            </button>
          </section>
        )}

        {/* ── Placement test nudge for new/low-progress learners ── */}
        {showPlacementBanner && (
          <section className="mb-8 flex flex-col items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-black text-amber-900">{t('placement.bannerTitle')}</p>
              <p className="text-xs text-amber-700">{t('placement.bannerDesc')}</p>
            </div>
            <button
              onClick={() => navigate('/placement-test')}
              className="shrink-0 rounded-xl border border-amber-300 bg-white px-4 py-2 text-sm font-bold text-amber-800 shadow-sm transition hover:bg-amber-100"
            >
              {t('placement.bannerCta')}
            </button>
          </section>
        )}

        {/* ── Daily mixed-skill mission ── */}
        <section className="mb-8 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-widest text-indigo-600">{t('streakBar.dailyGoal')}</p>
              <h2 className="text-lg font-black text-slate-900">{t('progress.todaysLearning')}</h2>
            </div>
            <span className="text-xs font-bold text-indigo-600">{pathProgress.missions.filter((m) => m.complete).length}/{pathProgress.missions.length} · {t('streakBar.done')}</span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {pathProgress.missions.map((mission) => (
              <div key={mission.step} className={`rounded-xl border px-3 py-2 ${mission.complete ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
                <p className="text-xs font-bold text-slate-700">{mission.complete ? '✓' : mission.icon} {mission.label}</p>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full bg-indigo-500" style={{ width: `${(mission.count / mission.goal) * 100}%` }} /></div>
                <p className="mt-1 text-[10px] text-slate-400">{mission.count}/{mission.goal}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Mobile learning-path progress ── */}
        <section className="mb-8 flex items-center gap-3 rounded-2xl border border-indigo-200 bg-white px-4 py-3 shadow-sm md:hidden">
          <div className="relative grid h-14 w-14 place-items-center rounded-full" style={{ background: `conic-gradient(#4f46e5 ${pathProgress.overall * 3.6}deg, #e2e8f0 0deg)` }}>
            <div className="grid h-10 w-10 place-items-center rounded-full bg-white text-xs font-black text-indigo-700">{pathProgress.overall}%</div>
          </div>
          <div>
            <p className="text-xs font-black text-slate-800">{t('progress.tenStepMastery')}</p>
            <p className="text-xs text-indigo-600">{pathProgress.mastery}</p>
          </div>
        </section>

        {/* ── Step-by-step learning path ── */}
        <section id="learn-section" className="mb-16">
          <div className="mb-6 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
            <p className="mb-2 text-xs font-black uppercase tracking-widest text-indigo-600">{t('home.startHere')}</p>
            <h2 className="text-3xl font-black text-slate-900">
              {config.learningPathTitle}
            </h2>
            <p className="mt-2 max-w-xl text-slate-600">
              {learningPathSubtitle}
            </p>
            </div>
            <div className="hidden items-center gap-3 rounded-2xl border border-indigo-200 bg-white px-4 py-3 shadow-sm md:flex">
              <div className="relative grid h-14 w-14 place-items-center rounded-full" style={{ background: `conic-gradient(#4f46e5 ${pathProgress.overall * 3.6}deg, #e2e8f0 0deg)` }}>
                <div className="grid h-10 w-10 place-items-center rounded-full bg-white text-xs font-black text-indigo-700">{pathProgress.overall}%</div>
              </div>
              <div>
                <p className="text-xs font-black text-slate-800">{t('progress.tenStepMastery')}</p>
                <p className="text-xs text-indigo-600">{pathProgress.mastery}</p>
              </div>
            </div>
          </div>

          {/* Desktop: two-row learning path grid */}
          <div className="hidden gap-4 md:grid md:grid-cols-2 lg:grid-cols-4">
            {visibleSteps.map((item) => (
              <div key={item.code} className="flex items-stretch">
                {/* Step card */}
                <div className={`flex-1 rounded-2xl border ${item.borderColor} ${item.bgColor} p-5 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg`}>
                  {/* Step badge */}
                  <div className="mb-4 flex items-center justify-between">
                    <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${item.badgeBg} text-xs font-black text-white`}>
                      {item.step}
                    </span>
                    <span className="text-2xl">{item.step >= 6 && !isAuthenticated ? '🔒' : item.icon}</span>
                  </div>

                  <p className={`text-sm font-black ${item.textColor}`}>{item.label}</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{item.desc}</p>
                  {item.step >= 6 && !isAuthenticated && <p className="mt-2 text-[10px] font-black uppercase tracking-wide text-slate-500">{t('auth.signIn')}</p>}
                  <div className="mt-3">
                    <div className="mb-1 flex justify-between text-[10px] font-bold text-slate-500"><span>{pathProgress.steps[item.code]?.mastery}</span><span>{pathProgress.steps[item.code]?.percent ?? 0}%</span></div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-white/80"><div className={`h-full ${item.badgeBg}`} style={{ width: `${pathProgress.steps[item.code]?.percent ?? 0}%` }} /></div>
                  </div>

                  <button
                    onClick={() => openStep(item)}

                    className={`mt-5 w-full rounded-xl ${item.btnClass} py-2.5 text-xs font-bold text-white shadow-sm transition`}
                  >
                    {item.step >= 6 && !isAuthenticated ? t('home.signInToUnlockBtn') : t('home.openBtn')}
                  </button>
                </div>

              </div>
            ))}
          </div>

          {/* Mobile: vertical stepper */}
          <div className="flex flex-col gap-0 md:hidden">
            {visibleSteps.map((item, index) => (
              <div key={item.code} className="flex gap-4">
                {/* Left: step line */}
                <div className="flex flex-col items-center">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${item.badgeBg} text-sm font-black text-white shadow-sm`}>
                    {item.step}
                  </span>
                  {index < visibleSteps.length - 1 && (
                    <div className="mt-1 h-full w-0.5 bg-slate-200" />
                  )}
                </div>

                {/* Right: card */}
                <div className={`mb-4 flex-1 rounded-2xl border ${item.borderColor} ${item.bgColor} p-4`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{item.step >= 6 && !isAuthenticated ? '🔒' : item.icon}</span>
                    <p className={`text-sm font-black ${item.textColor}`}>{item.label}</p>
                  </div>
                  <p className="text-xs leading-relaxed text-slate-500 mb-3">{item.desc}</p>
                  {item.step >= 6 && !isAuthenticated && <p className="mb-3 text-[10px] font-black uppercase tracking-wide text-slate-500">{t('auth.signIn')}</p>}
                  <div className="mb-3">
                    <div className="mb-1 flex justify-between text-[10px] font-bold text-slate-500"><span>{pathProgress.steps[item.code]?.mastery}</span><span>{pathProgress.steps[item.code]?.percent ?? 0}%</span></div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-white/80"><div className={`h-full ${item.badgeBg}`} style={{ width: `${pathProgress.steps[item.code]?.percent ?? 0}%` }} /></div>
                  </div>
                  <button
                    onClick={() => openStep(item)}

                    className={`w-full rounded-xl ${item.btnClass} py-2.5 text-xs font-bold text-white shadow-sm transition`}
                  >
                    {item.step >= 6 && !isAuthenticated ? t('home.signInToUnlockBtn') : t('home.openBtn')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Why it works ── */}
        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-6 md:p-10">
            <p className="mb-2 text-xs font-black uppercase tracking-widest text-indigo-600">{t('home.ourMethod')}</p>
            <h2 className="text-3xl font-black text-slate-900">{t('home.whyTitle')}</h2>
            <p className="mt-2 text-slate-600">{t('home.whySubtitle')}</p>
          </div>

          <div className="grid grid-cols-1 divide-y divide-slate-100 md:grid-cols-3 md:divide-x md:divide-y-0">
            {config.homeFeatures.map((f, i) => (
              <div key={f.title} className="relative p-6 md:p-8">
                <p className="absolute right-5 top-4 select-none text-6xl font-black text-slate-100">
                  {String(i + 1).padStart(2, '0')}
                </p>
                <div className="relative">
                  <span className="text-3xl">{f.icon}</span>
                  <h3 className="mt-3 font-bold text-slate-900">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* ── Footer ── */}
      <footer className="border-t border-slate-200 bg-white/60 py-8 backdrop-blur-sm">
        <div className="mx-auto max-w-6xl px-4 flex flex-col items-center gap-3 text-center sm:px-6 lg:px-8">
          <p className="text-xs text-slate-400">
            {t('home.footerText')} · &copy; {new Date().getFullYear()} Subram Miles
          </p>
          <div className="flex flex-wrap justify-center gap-5 text-xs">
            <Link to="/download" className="font-bold text-indigo-500 hover:text-indigo-700 transition">{t('nav.downloadApp')}</Link>
            <span className="text-slate-200">|</span>
            <Link to="/terms" className="text-slate-400 hover:text-indigo-600 transition">{t('profile.termsConditions')}</Link>
            <span className="text-slate-200">|</span>
            <Link to="/privacy" className="text-slate-400 hover:text-indigo-600 transition">{t('profile.privacyPolicy')}</Link>
            <span className="text-slate-200">|</span>
            <Link to="/credits" className="text-slate-400 hover:text-indigo-600 transition">{t('profile.credits')}</Link>
            <span className="text-slate-200">|</span>
            <Link to="/contact" className="text-slate-400 hover:text-indigo-600 transition">{t('home.contactUs')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
